import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';
import { SupplierDataArray, SupplierItem, SupplierModel, SupplierQueryParams } from '@app/interfaces/supplier';
import { createSupplierApi, deleteSupplierApi, getSupplierApi, getSuppliersApi, updateSupplierApi } from '@app/api/suppliers.api';

// export interface SupplierState {
//   data: SupplierModel[];
//   totalRows: number;
//   selectedRecord: SupplierModel[];
//   loading: boolean;
//   error: string | null;
// }

// const initialState: SupplierState = {
//   data: [],
//   totalRows: 0,
//   selectedRecord: [],
//   loading: false,
//   error: null,
// };

export interface CustomerState {
  data: SupplierModel[];
  totalRows: number;
    pagination: {
      current: number,
      pageSize: number
    },
    filters: any,
    sorter: any,
    searchTerm: string;
    selectedRecord: SupplierModel[];
    loading: boolean;
    error: string | null;
    filterData: {
      customerCode: string[];
      customerName: string[]; 
    };
    record: SupplierModel | null;
}

const initialState: CustomerState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  filterData: {
    customerCode: [],
    customerName: [], 
  },
  record: null,
};

interface ResponseData {
  data: SupplierItem;
  message: string;
}

// Thunks
export const getSuppliers = createAsyncThunk<SupplierDataArray, SupplierQueryParams>(
  'supplier/getSuppliers',
  async (params: SupplierQueryParams, { rejectWithValue }) => {
    try {
      const suppliers = (await getSuppliersApi(params)) as SupplierDataArray;
      return suppliers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getSupplier = createAsyncThunk<SupplierModel, number>(
  'supplier/getSupplier',
  async (supplierId, { rejectWithValue }) => {
    try {
      const data = await getSupplierApi(supplierId);
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const createSupplier = createAsyncThunk<ResponseData, SupplierModel>('supplier/createSupplier', async (data) => {
  const response = await createSupplierApi(data);
  return response;
});

export const updateSupplier = createAsyncThunk<ResponseData, { supplierId: number; formData: SupplierModel }>(
  'supplier/updateSupplier',
  async ({ supplierId, formData }) => {
    const response = await updateSupplierApi(supplierId, formData);
    return response;
  },
);

export const deleteSupplier = createAsyncThunk<{ supplierId: number; response: any }, number>(
  'supplier/deleteSupplier',
  async (supplierId, { rejectWithValue }) => {
    try {
      const response = await deleteSupplierApi(supplierId);
      return { supplierId, response };
    } catch (error) {
      // Handle errors with rejectWithValue
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const supplierSlice = createSlice({
  name: 'supplier',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSuppliers.fulfilled, (state, action) => {
 
        const { rows, total_rows } = action.payload;

        const data = rows;
        state.data = data.map((item: SupplierItem) => item.attributes) || [];

        state.totalRows = parseInt(total_rows, 10);
        state.loading = false;
      })
      .addCase(getSupplier.fulfilled, (state, action) => {
        const supplier = (action.payload as any).data;
        state.record = supplier.attributes;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        const dataId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

        const updatedIndex = state.data.findIndex((item) => item.id === dataId);
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        const { supplierId, response } = action.payload;

        state.data = state.data.filter((supplier) => supplier.id !== supplierId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload?.message || action.payload?.error || action?.error?.message);
      });
  },
});

export const { updateTableParams } = supplierSlice.actions;

export default supplierSlice.reducer;
